import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  Mic, MicOff, Video, VideoOff, Monitor,
  LayoutGrid, UserPlus, Settings, PhoneOff, Radio,
  ChevronUp, ChevronDown, Check, Globe, AppWindow, Heart, Languages, ArrowRight, X, Volume2, Captions,
  LogOut, UserX, User, SwitchCamera, FileText, Search, ShoppingBag, MonitorOff
} from 'lucide-react';
import MerchStorePanel from '../../meeting/components/MerchStorePanel';
import ExitIcon from '../../icons/Bottombar/ExitIcon';
import ReactionButton from './reactions/ReactionButton';
import { useSiteContext } from '../../context/SiteContext';
import { useMeetingAppContext } from '../../MeetingAppContextDef';
import { useMeetingState } from '../../context/MeetingStateContext';
import { usePubSub } from '../../config/sdkConfig';
import * as meetingServices from '../../services/meetingServices';
import CaptionsToggle from './translation/CaptionsToggle';
// getSpokenLanguagesFor / getCaptionLanguagesFor return the right list based
// on whether the Agora-translation fallback is active in this session:
//   - default (Google primary, working): full Google Translate language set
//   - Agora fallback active: limited Agora-supported subset
import { getLanguageById, getSpokenLanguagesFor, getCaptionLanguagesFor } from './translation/languageConfig';
import { getFlagDisplay } from './translation/FlagEmoji';
import SettingsModal from './settings/SettingsModal';
import RewardModal from './rewards/RewardModal';
import CallDurationTimer from './CallDurationTimer';
import { translationLanguages } from '../../utils/list';
import folderService from '../../services/folderServices';

// Layout options matching left sidebar order
const layouts = [
  { id: 'gallery', name: 'Gallery', icon: '◫◫' },
  { id: 'focus', name: 'Focus', icon: '▢' },
  { id: 'speaker-top', name: 'Speaker Top', icon: '▬' },
  { id: 'speaker-side', name: 'Speaker Side', icon: '◧' },
];

export default function StudioBottomControls({
  isMuted,
  isCameraOn,
  isScreenSharing,
  currentLayout,
  onToggleMic,
  onToggleCamera,
  onToggleScreenShare,
  onLayoutChange,
  onInvite,
  onLeave,
  onOpenSettings,
  // Screen share permission
  canScreenShare = true,
  // Reactions
  onSendReaction,
  onTriggerEffect,
  onRaiseHand,
  onToggleBRB,
  isHandRaised = false,
  isBRB = false,
  reactionsEnabled = true,
  boostedReactionsEnabled = true,
  allowRaiseHand = true,
  // Device selection
  audioInputs = [],
  audioOutputs = [],
  videoInputs = [],
  selectedAudioInput,
  selectedAudioOutput,
  selectedVideoInput,
  onAudioInputChange,
  onAudioOutputChange,
  onVideoInputChange,
  // Translation props
  captionsEnabled: externalCaptionsEnabled,
  onToggleCaptions: externalToggleCaptions,
  spokenLanguage: externalSpokenLanguage,
  captionLanguage: externalCaptionLanguage,
  onSpokenLanguageChange,
  onCaptionLanguageChange,
  // Translation Tools Panel
  onOpenTranslationTools,
  // AI Captions (Custom speech recognition)
  onStartAICaptions,
  isAICaptionsActive = false,
  // Translation provider state — when true, Google Translate has failed for
  // this session and BOTH language pickers should restrict their options to
  // the Agora-supported subset.
  agoraFallbackActive = false,
  // When true, the user picked a spoken language that Agora STT cannot
  // transcribe and we auto-reverted to English. The spoken-language picker
  // narrows to the Agora-transcribable subset; caption picker is unaffected.
  restrictSpokenToAgora = false,
  // AI Notetaker / Transcription (separate from CC captions)
  onToggleTranscription,
  isTranscriptionActive = false,
  canToggleTranscription = false,
  // VideoSDK Translation (AI Captions from top menu)
  translationEnabled = false,
  setTranslationEnabled,
  translationStatus = 'stopped',
  startTranslation,
  stopTranslation,
  changeTranslationLanguage,
  selectedLanguage = 'en',
  setSelectedLanguage,
  // Virtual background props
  blurEnabled = false,
  setBlurEnabled,
  blurStrength = 50,
  setBlurStrength,
  selectedBackground = null,
  handleStartVirtualBackground,
  virtualBackgroundOn = false,
  // Green screen props
  greenScreenEnabled = false,
  handleStartGreenScreen,
  // Local webcam stream for preview
  localWebcamStream,
  localWebcamOn = false,
  processedWebcamStream = null,
  // Meeting stats (ref to avoid parent re-renders)
  localParticipantStatsRef = null,
  // Camera resolution callback
  onCameraResolutionChange,
  // Audio settings callback
  onAudioSettingsChange,
  // AI Noise cancellation props
  noiseCancellationEnabled = true,
  noiseCancellationMode = 'NSNG',
  onNoiseCancellationToggle,
  onNoiseCancellationModeChange,
  // Call duration timer
  meetingStartTime,
  // Right sidebar state
  rightSidebarCollapsed = true,
  // Live streaming
  isLiveStreaming = false,
  showGoLiveButton = false,
  // Exit meeting props
  onEndForAll,
  onEndForAllExceptHost,
  onLeaveAndAssignHost,
  isHost = false,
  participants = null,
  // Webinar viewer mode — hides mic, camera, screen share for attendees
  isWebinarViewer = false,
  // Waiting room - admit all when disabling
  admitAllWaitingParticipants,
  // Co-hosts and presenters sets for end-except-host logic
  coHosts = new Set(),
  presenters = new Set(),
}) {
  const { meetingSettings, setMeetingSettings, meetingRole, isAuthenticated, meetingSession, user } = useSiteContext();
  const { gridPagination } = useMeetingAppContext();
  const { emitForceMuteAll, emitGrantUnmuteAll } = useMeetingState();
  const { publish: publishMeetingSettings } = usePubSub("MEETING_SETTINGS");

  const waitingRoomEnabled = meetingSettings?.hostApprovalRequired || meetingSettings?.waitingRoom;
  const autoAdmitRegistered = meetingSettings?.autoAdmitRegistered ?? false;

  // Room context for virtual background uploads
  const roomId = meetingSettings?.roomId;

  const handleWaitingRoomChange = (newValue) => {
    // When disabling waiting room, admit all waiting participants BEFORE
    // state update (which disconnects the host waiting room socket)
    if (!newValue && admitAllWaitingParticipants) {
      admitAllWaitingParticipants();
    }
    // When disabling waiting room, also disable auto-admit (it depends on waiting room)
    const updates = { hostApprovalRequired: newValue, waitingRoom: newValue };
    if (!newValue) updates.autoAdmitRegistered = false;
    setMeetingSettings(prev => ({ ...prev, ...updates }));
    publishMeetingSettings({ data: { ...meetingSettings, ...updates } });
    if (isAuthenticated) {
      meetingServices.updateMeetingSessionSettings({
        meetingId: meetingSettings?.roomId,
        sessionId: meetingSession,
        guestCanMute: meetingSettings?.guestCanUnmute,
        waitingRoomEnabled: newValue,
      });
      // Persist to DB
      const formData = new FormData();
      formData.append('waitingRoom', newValue);
      if (!newValue) formData.append('autoAdmitRegistered', false);
      const roomDbId = meetingSettings?.id || meetingSettings?._id;
      if (roomDbId) {
        meetingServices.updateRoomSetting(roomDbId, { waitingRoom: newValue, ...(newValue ? {} : { autoAdmitRegistered: false }) }).catch(() => {});
      }
    }
  };

  const handleAutoAdmitRegisteredChange = async (newValue) => {
    // When enabling auto-admit, also enable waiting room
    const updates = { autoAdmitRegistered: newValue };
    if (newValue && !waitingRoomEnabled) {
      updates.waitingRoom = true;
      updates.hostApprovalRequired = true;
    }
    setMeetingSettings(prev => ({ ...prev, ...updates }));
    publishMeetingSettings({ data: { ...meetingSettings, ...updates } });
    const roomDbId = meetingSettings?.id || meetingSettings?._id;
    if (roomDbId) {
      meetingServices.updateRoomSetting(roomDbId, {
        autoAdmitRegistered: newValue,
        ...(newValue && !waitingRoomEnabled ? { waitingRoom: true } : {})
      }).catch((e) => {
        console.error('Failed to update auto-admit setting:', e);
        setMeetingSettings(prev => ({ ...prev, autoAdmitRegistered: !newValue }));
      });
    }
  };

  const handleGuestCanUnmuteChange = (newValue) => {
    setMeetingSettings(prev => ({ ...prev, guestCanUnmute: newValue }));
    publishMeetingSettings({ data: { ...meetingSettings, guestCanUnmute: newValue } });
    if (isAuthenticated) {
      meetingServices.updateMeetingSessionSettings({
        meetingId: meetingSettings?.roomId,
        sessionId: meetingSession,
        guestCanMute: newValue,
        waitingRoomEnabled: meetingSettings?.hostApprovalRequired || meetingSettings?.waitingRoom,
      });
    }
    localStorage.setItem("guest-can-unmute-" + meetingSession, newValue);
    if (!newValue) emitForceMuteAll();
    else emitGrantUnmuteAll();
  };

  const handleMuteOnEntryChange = (newValue) => {
    setMeetingSettings(prev => ({ ...prev, muteParticipants: newValue }));
    publishMeetingSettings({ data: { ...meetingSettings, muteParticipants: newValue } });
    const roomDbId = meetingSettings?.id || meetingSettings?._id;
    if (roomDbId) {
      meetingServices.updateRoomSetting(roomDbId, { muteParticipantsOnEntry: newValue }).catch(() => {});
    }
  };

  // Go Live button handler
  // If credentials are saved from scheduling, dispatch directStart to begin streaming immediately
  // Otherwise, open the live stream modal for manual credential entry
  const handleGoLiveClick = () => {
    if (isLiveStreaming) {
      console.log('[RTMP] Stop Live button clicked');
      window.dispatchEvent(new CustomEvent('rlink-livestream-action', { detail: { action: 'stop' } }));
    } else {
      const hasCreds = meetingSettings?.streamYouTubeKey || meetingSettings?.streamFacebookKey;
      console.log('[RTMP] Go Live button clicked, hasCreds:', !!hasCreds);
      window.dispatchEvent(new CustomEvent('rlink-livestream-action', { detail: { action: hasCreds ? 'directStart' : 'openModal' } }));
    }
  };
  // The "Allow Screen Sharing" room setting only restricts attendees —
  // hosts/co-hosts/speakers/presenters can always share regardless. The
  // canScreenShare prop already encodes the role + dynamic-presenter check
  // performed in the parent (MeetingContainerNew).
  const isHostOrPresenterRole = ["moderator", "co-host", "host", "speaker", "presenter"].includes(meetingRole);
  const isScreenSharingAllowed = meetingSettings?.allowScreenSharing !== false || isHostOrPresenterRole || canScreenShare;
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);
  const [showMicPicker, setShowMicPicker] = useState(false);
  const [showSpeakerPicker, setShowSpeakerPicker] = useState(false);
  const [showCameraPicker, setShowCameraPicker] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showExitMenu, setShowExitMenu] = useState(false);
  const [showAssignHost, setShowAssignHost] = useState(false);
  const [showEndExceptHostWarning, setShowEndExceptHostWarning] = useState(false);
  const exitBtnRef = useRef(null);

  // Get remote participants for host assignment
  const remoteParticipants = useMemo(() => {
    if (!participants) return [];
    const list = [];
    const pMap = participants instanceof Map ? participants : new Map(participants);
    pMap.forEach((p, id) => {
      if (!p.isLocal) {
        list.push({ id, displayName: p.displayName || p.metaData?.displayName || 'Participant' });
      }
    });
    return list;
  }, [participants]);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [shareSystemAudio, setShareSystemAudio] = useState(true);
  const [showSpokenLangDropdown, setShowSpokenLangDropdown] = useState(false);
  const [showCaptionLangDropdown, setShowCaptionLangDropdown] = useState(false);
  // Search filter inputs for the spoken / caption language dropdowns. The
  // language lists are large enough now (full Google Translate set) that a
  // lookup is essential.
  const [spokenLangQuery, setSpokenLangQuery] = useState('');
  const [captionLangQuery, setCaptionLangQuery] = useState('');

  // Source lists depend on whether either fallback flag is active:
  //   - agoraFallbackActive   → restricts BOTH pickers (Google Translate failed)
  //   - restrictSpokenToAgora → restricts SPOKEN picker only (auto-revert flow)
  const spokenLanguageList = useMemo(
    () => getSpokenLanguagesFor(agoraFallbackActive || restrictSpokenToAgora),
    [agoraFallbackActive, restrictSpokenToAgora]
  );
  const captionLanguageList = useMemo(
    () => getCaptionLanguagesFor(agoraFallbackActive),
    [agoraFallbackActive]
  );

  const filteredSpokenLanguages = useMemo(() => {
    const q = spokenLangQuery.trim().toLowerCase();
    if (!q) return spokenLanguageList;
    return spokenLanguageList.filter(l =>
      l.label.toLowerCase().includes(q) || l.id.toLowerCase().includes(q)
    );
  }, [spokenLangQuery, spokenLanguageList]);

  const filteredCaptionLanguages = useMemo(() => {
    const q = captionLangQuery.trim().toLowerCase();
    if (!q) return captionLanguageList;
    return captionLanguageList.filter(l =>
      l.label.toLowerCase().includes(q) || l.id.toLowerCase().includes(q)
    );
  }, [captionLangQuery, captionLanguageList]);
  const [translationPopupPos, setTranslationPopupPos] = useState({ x: 0, y: 0 });
  const [layoutPopupPos, setLayoutPopupPos] = useState({ x: 0, y: 0 });
  const [sharePopupPos, setSharePopupPos] = useState({ x: 0, y: 0 });
  const [micPopupPos, setMicPopupPos] = useState({ x: 0, y: 0 });
  const [speakerPopupPos, setSpeakerPopupPos] = useState({ x: 0, y: 0 });
  const [cameraPopupPos, setCameraPopupPos] = useState({ x: 0, y: 0 });
  const translationBtnRef = useRef(null);
  const [showTranscriptionPopup, setShowTranscriptionPopup] = useState(false);
  const [transcriptionPopupPos, setTranscriptionPopupPos] = useState({ x: 0, y: 0 });
  const [showMerchStore, setShowMerchStore] = useState(false);
  const [merchStoreActive, setMerchStoreActive] = useState(false);
  const [availableShowcases, setAvailableShowcases] = useState([]);
  const [selectedShowcaseId, setSelectedShowcaseId] = useState(null);
  const [displayedProducts, setDisplayedProducts] = useState([]);

  // Merch Store is still in testing — only admin accounts can control it.
  // Non-admin attendees still see the shopping-bag icon once an admin host
  // toggles "Show to attendees" on (the loose-gating decision).
  const canControlMerchStore = !!user?.isAdmin && ['host', 'co-host', 'moderator'].includes(meetingRole);

  // Keep refs of canControl + active + selected so the REQUEST listener can
  // read current values without re-subscribing on every render.
  const canControlRef = useRef(canControlMerchStore);
  const merchStoreActiveRef = useRef(merchStoreActive);
  const displayedProductsRef = useRef(displayedProducts);
  useEffect(() => { canControlRef.current = canControlMerchStore; }, [canControlMerchStore]);
  useEffect(() => { merchStoreActiveRef.current = merchStoreActive; }, [merchStoreActive]);
  useEffect(() => { displayedProductsRef.current = displayedProducts; }, [displayedProducts]);

  // Fetch the admin's Product Showcase elements so they can pick which one
  // to display. Temporary: auto-selects the first one (Phase 2 will add a
  // proper "default collection" field marked via a star icon).
  useEffect(() => {
    if (!canControlMerchStore) return;
    let cancelled = false;
    (async () => {
      try {
        const folders = await folderService.getFolders();
        const foldersList = Array.isArray(folders) ? folders : [];
        const showcases = [];
        for (const folder of foldersList) {
          try {
            const elems = await folderService.getCtaByFolderId(folder._id);
            const list = Array.isArray(elems) ? elems : [];
            list.forEach((el) => {
              if (el.type === 'productshowcase' || el.type === 'product_showcase') {
                let settings = {};
                try {
                  settings = typeof el.settings === 'string' ? JSON.parse(el.settings) : (el.settings || {});
                } catch (_) { settings = {}; }
                showcases.push({
                  id: el._id,
                  name: el.name || 'Untitled Collection',
                  products: Array.isArray(settings.products) ? settings.products : [],
                  isDefault: !!el.isDefault,
                });
              }
            });
          } catch (_) { /* skip folder on error */ }
        }
        if (cancelled) return;
        setAvailableShowcases(showcases);
        if (showcases.length > 0) {
          // Pick the user's default showcase if they've marked one,
          // otherwise fall back to the first one in the list.
          const defaultShowcase = showcases.find((s) => s.isDefault) || showcases[0];
          setSelectedShowcaseId((prev) => prev || defaultShowcase.id);
          setDisplayedProducts(defaultShowcase.products);
        }
      } catch (err) {
        console.error('[MerchStore] Failed to load showcases:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [canControlMerchStore]);

  const { publish: publishMerchStoreVisible } = usePubSub("MERCH_STORE_VISIBLE", {
    onMessageReceived: (data) => {
      if (data?.isLocal) return;
      const active = !!data?.message?.active;
      setMerchStoreActive(active);
      if (!active) setShowMerchStore(false);
    },
  });

  // Host broadcasts the active product list to attendees. Also persisted for
  // late joiners via the REQUEST/RESPONSE fallback below.
  const { publish: publishMerchStoreProducts } = usePubSub("MERCH_STORE_PRODUCTS", {
    onMessageReceived: (data) => {
      if (data?.isLocal) return;
      const products = Array.isArray(data?.message?.products) ? data.message.products : [];
      setDisplayedProducts(products);
    },
  });

  // Late-joiner sync: attendees publish a REQUEST on mount; any host/moderator
  // replies by re-publishing the current state. This is belt-and-braces on
  // top of persist:true so the button appears for late joiners even if the
  // persisted channel attribute race-conditions the mount.
  const { publish: publishMerchStoreRequest } = usePubSub("MERCH_STORE_REQUEST", {
    onMessageReceived: (data) => {
      if (data?.isLocal) return;
      if (!canControlRef.current) return;
      publishMerchStoreVisible(
        { active: !!merchStoreActiveRef.current },
        { persist: true }
      );
      publishMerchStoreProducts(
        { products: displayedProductsRef.current || [] },
        { persist: true }
      );
    },
  });

  // On mount, non-controllers ask the room for the current state.
  useEffect(() => {
    if (canControlMerchStore) return;
    const t = setTimeout(() => {
      publishMerchStoreRequest({ requesting: true });
    }, 1500);
    return () => clearTimeout(t);
  }, [canControlMerchStore, publishMerchStoreRequest]);

  // Opens / closes only MY panel. Does not affect anyone else.
  const handleMerchStoreClick = () => {
    setShowMerchStore((v) => !v);
  };

  // Host-only: broadcasts whether the Merch Store button should appear in
  // attendees' bottom bars. Called from the toggle inside the panel header.
  const handleToggleMerchStoreActive = () => {
    if (!canControlMerchStore) return;
    const next = !merchStoreActive;
    setMerchStoreActive(next);
    publishMerchStoreVisible({ active: next }, { persist: true });
    if (next) {
      // Push the current products so attendees see the right collection
      publishMerchStoreProducts(
        { products: displayedProducts || [] },
        { persist: true }
      );
    }
  };

  // Host-only: switch to a different Product Showcase. Updates local state
  // and broadcasts the new product list to everyone in the meeting.
  const handleSelectShowcase = (showcaseId) => {
    if (!canControlMerchStore) return;
    const showcase = availableShowcases.find((s) => s.id === showcaseId);
    if (!showcase) return;
    setSelectedShowcaseId(showcaseId);
    setDisplayedProducts(showcase.products);
    if (merchStoreActive) {
      publishMerchStoreProducts(
        { products: showcase.products },
        { persist: true }
      );
    }
  };
  const transcriptionBtnRef = useRef(null);
  const transcriptionPopupRef = useRef(null);
  const layoutBtnRef = useRef(null);
  const shareBtnRef = useRef(null);
  const micBtnRef = useRef(null);
  const speakerBtnRef = useRef(null);
  const cameraBtnRef = useRef(null);

  // Internal translation state (used if no external props provided)
  const [internalCaptionsEnabled, setInternalCaptionsEnabled] = useState(false);
  const [internalSpokenLanguage, setInternalSpokenLanguage] = useState('en');
  const [internalCaptionLanguage, setInternalCaptionLanguage] = useState('en');

  // Use external values if provided, otherwise use internal state
  const captionsEnabled = externalCaptionsEnabled !== undefined ? externalCaptionsEnabled : internalCaptionsEnabled;
  // Use internal state (updated immediately on change) for instant UI feedback
  const spokenLanguage = internalSpokenLanguage;
  const captionLanguage = internalCaptionLanguage;

  // Sync internal state when external props change (e.g. from other components)
  useEffect(() => {
    if (externalSpokenLanguage) setInternalSpokenLanguage(externalSpokenLanguage);
  }, [externalSpokenLanguage]);
  useEffect(() => {
    if (externalCaptionLanguage) setInternalCaptionLanguage(externalCaptionLanguage);
  }, [externalCaptionLanguage]);

  const handleToggleCaptions = () => {
    if (externalToggleCaptions) {
      externalToggleCaptions();
    } else {
      setInternalCaptionsEnabled(!internalCaptionsEnabled);
    }
  };

  const handleSpokenLanguageChange = (lang) => {
    // Always update local state immediately for instant UI feedback
    setInternalSpokenLanguage(lang);
    if (onSpokenLanguageChange) {
      onSpokenLanguageChange(lang);
    }
  };

  const handleCaptionLanguageChange = (lang) => {
    // Always update local state immediately for instant UI feedback
    setInternalCaptionLanguage(lang);
    if (onCaptionLanguageChange) {
      onCaptionLanguageChange(lang);
    }
  };

  // Close popovers when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.popover-container')) {
        setShowLayoutPicker(false);
        setShowMicPicker(false);
        setShowSpeakerPicker(false);
        setShowCameraPicker(false);
        setShowShareOptions(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Close translation popup when clicking outside (it's a portal so needs its own handler)
  const translationPopupRef = useRef(null);
  useEffect(() => {
    if (!showTranslation) return;
    const handleClickOutside = (e) => {
      // Don't close if clicking the CC button itself (it toggles)
      if (translationBtnRef.current && translationBtnRef.current.contains(e.target)) return;
      // Don't close if clicking inside the popup
      if (translationPopupRef.current && translationPopupRef.current.contains(e.target)) return;
      setShowTranslation(false);
      setShowSpokenLangDropdown(false);
      setShowCaptionLangDropdown(false);
    };
    // Use setTimeout to avoid the same click that opened it from closing it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTranslation]);

  // Close transcription popup when clicking outside (it's a portal so needs its own handler)
  useEffect(() => {
    if (!showTranscriptionPopup) return;
    const handleClickOutside = (e) => {
      if (transcriptionBtnRef.current && transcriptionBtnRef.current.contains(e.target)) return;
      if (transcriptionPopupRef.current && transcriptionPopupRef.current.contains(e.target)) return;
      setShowTranscriptionPopup(false);
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTranscriptionPopup]);

  return (
    <div className="h-14 md:h-16 bg-[#001233] border-t border-white/10 flex items-center px-2 md:px-4 overflow-x-auto relative z-50">
      {/* Spacer for left side to keep controls centered */}
      <div className="hidden sm:flex items-center min-w-[100px]" />
      <div className="flex items-center gap-1 md:gap-1 mx-auto">
        {/* Mic Button with Device Picker */}
        {!isWebinarViewer && <div className="relative popover-container">
          <div className="flex items-center">
            <button
              onClick={onToggleMic}
              className={`p-2.5 rounded-l-lg transition-all ${
                isMuted
                  ? 'bg-red-500/80 hover:bg-red-500'
                  : 'hover:bg-white/10'
              }`}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <MicOff className="w-5 h-5 text-white" />
              ) : (
                <Mic className="w-5 h-5 text-white" />
              )}
            </button>
            <button
              ref={micBtnRef}
              onClick={(e) => {
                e.stopPropagation();
                if (micBtnRef.current) {
                  const rect = micBtnRef.current.getBoundingClientRect();
                  setMicPopupPos({
                    x: rect.left,
                    y: rect.top - 8
                  });
                }
                // Close other popups
                setShowSpeakerPicker(false);
                setShowCameraPicker(false);
                setShowShareOptions(false);
                setShowLayoutPicker(false);
                setShowTranslation(false);

                setShowMicPicker(!showMicPicker);
              }}
              className={`px-1 py-2.5 rounded-r-lg border-l border-white/20 transition-all ${
                isMuted ? 'bg-red-500/80 hover:bg-red-500' : 'hover:bg-white/10'
              }`}
              title="Select microphone"
            >
              <ChevronUp className="w-3 h-3 text-white" />
            </button>
          </div>
        </div>}

        {/* Speaker Output Button with Device Picker */}
        {!isWebinarViewer && <div className="relative popover-container">
          <div className="flex items-center">
            <button
              className="p-2.5 rounded-l-lg transition-all hover:bg-white/10"
              title="Speaker output"
            >
              <Volume2 className="w-5 h-5 text-white" />
            </button>
            <button
              ref={speakerBtnRef}
              onClick={(e) => {
                e.stopPropagation();
                if (speakerBtnRef.current) {
                  const rect = speakerBtnRef.current.getBoundingClientRect();
                  setSpeakerPopupPos({
                    x: rect.left,
                    y: rect.top - 8
                  });
                }
                // Close other popups
                setShowMicPicker(false);
                setShowCameraPicker(false);
                setShowShareOptions(false);
                setShowLayoutPicker(false);
                setShowTranslation(false);

                setShowSpeakerPicker(!showSpeakerPicker);
              }}
              className="px-1 py-2.5 rounded-r-lg border-l border-white/20 transition-all hover:bg-white/10"
              title="Select speaker output"
            >
              <ChevronUp className="w-3 h-3 text-white" />
            </button>
          </div>
        </div>}

        {/* Camera Button with Device Picker */}
        {!isWebinarViewer && <div className="relative popover-container">
          <div className="flex items-center">
            <button
              onClick={onToggleCamera}
              className={`p-2.5 rounded-l-lg transition-all ${
                !isCameraOn
                  ? 'bg-red-500/80 hover:bg-red-500'
                  : 'hover:bg-white/10'
              }`}
              title={isCameraOn ? 'Turn off camera' : 'Turn on camera'}
            >
              {isCameraOn ? (
                <Video className="w-5 h-5 text-white" />
              ) : (
                <VideoOff className="w-5 h-5 text-white" />
              )}
            </button>
            <button
              ref={cameraBtnRef}
              onClick={(e) => {
                e.stopPropagation();
                if (cameraBtnRef.current) {
                  const rect = cameraBtnRef.current.getBoundingClientRect();
                  setCameraPopupPos({
                    x: rect.left,
                    y: rect.top - 8
                  });
                }
                // Close other popups
                setShowMicPicker(false);
                setShowSpeakerPicker(false);
                setShowShareOptions(false);
                setShowLayoutPicker(false);
                setShowTranslation(false);

                setShowCameraPicker(!showCameraPicker);
              }}
              className={`px-1 py-2.5 rounded-r-lg border-l border-white/20 transition-all ${
                !isCameraOn ? 'bg-red-500/80 hover:bg-red-500' : 'hover:bg-white/10'
              }`}
              title="Select camera"
            >
              <ChevronUp className="w-3 h-3 text-white" />
            </button>
          </div>
        </div>}

        {/* Screen Share Button with Options */}
        {!isWebinarViewer && isScreenSharingAllowed && <div className="relative popover-container">
          <div className="flex items-center">
            <button
              onClick={canScreenShare ? onToggleScreenShare : undefined}
              className={`p-2.5 rounded-l-lg transition-all ${
                !canScreenShare
                  ? 'opacity-50 cursor-not-allowed'
                  : isScreenSharing
                  ? 'bg-[#6a1fbf] hover:bg-[#5a1aaf]'
                  : 'hover:bg-white/10'
              }`}
              title={!canScreenShare ? 'Only hosts or presenters can share screen' : isScreenSharing ? 'Stop sharing' : 'Share screen'}
              disabled={!canScreenShare}
            >
              <Monitor className="w-5 h-5 md:w-4 md:h-4 text-white" />
            </button>
            <button
              ref={shareBtnRef}
              onClick={(e) => {
                e.stopPropagation();
                if (shareBtnRef.current) {
                  const rect = shareBtnRef.current.getBoundingClientRect();
                  setSharePopupPos({
                    x: rect.left - 200, // Position to the left of button
                    y: rect.top - 8
                  });
                }
                // Close other popups
                setShowMicPicker(false);
                setShowSpeakerPicker(false);
                setShowCameraPicker(false);
                setShowLayoutPicker(false);
                setShowTranslation(false);

                setShowShareOptions(!showShareOptions);
              }}
              className={`px-1 py-2.5 rounded-r-lg border-l border-white/20 transition-all ${
                isScreenSharing ? 'bg-[#6a1fbf] hover:bg-[#5a1aaf]' : 'hover:bg-white/10'
              }`}
              title="Share options"
            >
              <ChevronUp className="w-3 h-3 text-white" />
            </button>
          </div>
        </div>}

        {/* Microphone Picker Popup - Rendered as Portal */}
        {showMicPicker && ReactDOM.createPortal(
          <div
            className="fixed w-64 p-2 bg-[#001233]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl z-[99999]"
            style={{
              left: micPopupPos.x,
              bottom: window.innerHeight - micPopupPos.y,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-xs text-gray-400 mb-2 px-2">{audioInputs.length === 0 ? 'No Microphone Found' : 'Select Microphone'}</p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {audioInputs.length === 0 ? (
                <p className="text-xs text-gray-500 px-2">Please connect a microphone</p>
              ) : (
                audioInputs.map(device => (
                  <button
                    key={device.deviceId}
                    onClick={() => {
                      onAudioInputChange?.(device.deviceId);
                      setShowMicPicker(false);
                    }}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-xs transition-colors ${
                      selectedAudioInput === device.deviceId
                        ? 'bg-indigo-500/20 text-white'
                        : 'text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    {selectedAudioInput === device.deviceId && (
                      <Check className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                    )}
                    <span className="truncate">{device.label || `Microphone ${device.deviceId.slice(0, 8)}`}</span>
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body
        )}

        {/* Speaker Output Picker Popup - Rendered as Portal */}
        {showSpeakerPicker && ReactDOM.createPortal(
          <div
            className="fixed w-64 p-2 bg-[#001233]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl z-[99999]"
            style={{
              left: speakerPopupPos.x,
              bottom: window.innerHeight - speakerPopupPos.y,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-xs text-gray-400 mb-2 px-2">Select Speaker Output</p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {audioOutputs.length === 0 ? (
                <p className="text-xs text-gray-500 px-2">No speakers found</p>
              ) : (
                audioOutputs.map(device => (
                  <button
                    key={device.deviceId}
                    onClick={() => {
                      onAudioOutputChange?.(device.deviceId);
                      setShowSpeakerPicker(false);
                    }}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-xs transition-colors ${
                      selectedAudioOutput === device.deviceId
                        ? 'bg-indigo-500/20 text-white'
                        : 'text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    {selectedAudioOutput === device.deviceId && (
                      <Check className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                    )}
                    <span className="truncate">{device.label || `Speaker ${device.deviceId.slice(0, 8)}`}</span>
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body
        )}

        {/* Camera Picker Popup - Rendered as Portal */}
        {showCameraPicker && ReactDOM.createPortal(
          <div
            className="fixed w-64 p-2 bg-[#001233]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl z-[99999]"
            style={{
              left: cameraPopupPos.x,
              bottom: window.innerHeight - cameraPopupPos.y,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-xs text-gray-400 mb-2 px-2">Select Camera</p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {videoInputs.length === 0 ? (
                <p className="text-xs text-gray-500 px-2">No cameras found</p>
              ) : (
                videoInputs.map(device => (
                  <button
                    key={device.deviceId}
                    onClick={() => {
                      onVideoInputChange?.(device.deviceId);
                      setShowCameraPicker(false);
                    }}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-xs transition-colors ${
                      selectedVideoInput === device.deviceId
                        ? 'bg-indigo-500/20 text-white'
                        : 'text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    {selectedVideoInput === device.deviceId && (
                      <Check className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                    )}
                    <span className="truncate">{device.label || `Camera ${device.deviceId.slice(0, 8)}`}</span>
                  </button>
                ))
              )}
            </div>
            {/* Switch Camera (front/back) */}
            {videoInputs.length >= 2 && (
              <>
                <div className="border-t border-white/10 my-2" />
                <button
                  onClick={() => {
                    const currentIdx = videoInputs.findIndex(d => d.deviceId === selectedVideoInput);
                    const nextIdx = (currentIdx + 1) % videoInputs.length;
                    onVideoInputChange?.(videoInputs[nextIdx].deviceId);
                    setShowCameraPicker(false);
                  }}
                  className="w-full flex items-center gap-2 p-2 rounded-lg text-left text-xs text-white hover:bg-white/10 transition-colors"
                >
                  <SwitchCamera className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                  <span>Switch Camera</span>
                </button>
              </>
            )}
          </div>,
          document.body
        )}

        {/* Screen Share Options Popup - Rendered as Portal */}
        {showShareOptions && ReactDOM.createPortal(
          <div
            className="fixed w-56 p-2 bg-[#001233]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl z-[99999]"
            style={{
              left: sharePopupPos.x,
              bottom: window.innerHeight - sharePopupPos.y,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-xs text-gray-400 mb-2 px-2">Share Options</p>
            {!canScreenShare && (
              <p className="text-xs text-yellow-400 mb-2 px-2">Only hosts or presenters can share</p>
            )}
            {isScreenSharing ? (
              // While a share is in progress, the only meaningful action is to
              // stop it. Picking a new share target requires stopping first
              // (browsers don't allow swapping a live getDisplayMedia source),
              // and the audio toggle can't be applied mid-stream — so collapse
              // the menu to a single Stop action and a disabled audio toggle.
              <div className="space-y-1">
                <button
                  onClick={() => {
                    onToggleScreenShare?.();
                    setShowShareOptions(false);
                  }}
                  className="w-full flex items-center gap-3 p-2 rounded-lg text-left text-xs text-gray-300 hover:bg-red-500/10 transition-colors"
                >
                  <MonitorOff className="w-4 h-4 text-red-400" />
                  <div>
                    <p className="text-white">Stop Sharing</p>
                    <p className="text-[10px] text-gray-500">End the current screen share</p>
                  </div>
                </button>
                <div className="border-t border-white/10 my-2" />
                <div
                  className="w-full flex items-center justify-between p-2 rounded-lg text-left text-xs text-gray-500 opacity-50 cursor-not-allowed"
                  title="Stop sharing first to change this"
                >
                  <div className="flex items-center gap-3">
                    <Volume2 className="w-4 h-4 text-gray-500" />
                    <span>Share System Audio</span>
                  </div>
                  <div className={`w-8 h-4 rounded-full ${shareSystemAudio ? 'bg-indigo-500/40' : 'bg-gray-700'}`}>
                    <div className={`w-3 h-3 rounded-full bg-white/60 mt-0.5 ${shareSystemAudio ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <button
                  onClick={() => {
                    if (canScreenShare) {
                      onToggleScreenShare?.();
                      setShowShareOptions(false);
                    }
                  }}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg text-left text-xs transition-colors ${
                    canScreenShare ? 'text-gray-300 hover:bg-white/10' : 'text-gray-500 opacity-50 cursor-not-allowed'
                  }`}
                  disabled={!canScreenShare}
                >
                  <Monitor className={`w-4 h-4 ${canScreenShare ? 'text-indigo-400' : 'text-gray-500'}`} />
                  <div>
                    <p className={canScreenShare ? 'text-white' : 'text-gray-500'}>Entire Screen</p>
                    <p className="text-[10px] text-gray-500">Share your full screen</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    if (canScreenShare) {
                      onToggleScreenShare?.();
                      setShowShareOptions(false);
                    }
                  }}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg text-left text-xs transition-colors ${
                    canScreenShare ? 'text-gray-300 hover:bg-white/10' : 'text-gray-500 opacity-50 cursor-not-allowed'
                  }`}
                  disabled={!canScreenShare}
                >
                  <AppWindow className={`w-4 h-4 ${canScreenShare ? 'text-green-400' : 'text-gray-500'}`} />
                  <div>
                    <p className={canScreenShare ? 'text-white' : 'text-gray-500'}>Application Window</p>
                    <p className="text-[10px] text-gray-500">Share a specific app</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    if (canScreenShare) {
                      onToggleScreenShare?.();
                      setShowShareOptions(false);
                    }
                  }}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg text-left text-xs transition-colors ${
                    canScreenShare ? 'text-gray-300 hover:bg-white/10' : 'text-gray-500 opacity-50 cursor-not-allowed'
                  }`}
                  disabled={!canScreenShare}
                >
                  <Globe className={`w-4 h-4 ${canScreenShare ? 'text-blue-400' : 'text-gray-500'}`} />
                  <div>
                    <p className={canScreenShare ? 'text-white' : 'text-gray-500'}>Browser Tab</p>
                    <p className="text-[10px] text-gray-500">Share a Chrome tab</p>
                  </div>
                </button>
                <div className="border-t border-white/10 my-2" />
                <button
                  onClick={() => setShareSystemAudio(!shareSystemAudio)}
                  className="w-full flex items-center justify-between p-2 rounded-lg text-left text-xs text-gray-300 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Volume2 className="w-4 h-4 text-orange-400" />
                    <span className="text-white">Share System Audio</span>
                  </div>
                  <div className={`w-8 h-4 rounded-full transition-colors ${shareSystemAudio ? 'bg-indigo-500' : 'bg-gray-600'}`}>
                    <div className={`w-3 h-3 rounded-full bg-white mt-0.5 transition-transform ${shareSystemAudio ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </button>
              </div>
            )}
          </div>,
          document.body
        )}

        {/* Reactions Button - always shown for Status section (Raise Hand, BRB) */}
        <ReactionButton
          onTriggerEffect={onTriggerEffect}
          onSendReaction={onSendReaction}
          onRaiseHand={onRaiseHand}
          onToggleBRB={onToggleBRB}
          isHandRaised={isHandRaised}
          isBRB={isBRB}
          reactionsEnabled={reactionsEnabled}
          boostedReactionsEnabled={boostedReactionsEnabled}
          allowRaiseHand={allowRaiseHand}
        />

        {/* Reward Button */}
        <button
          onClick={() => setShowRewardModal(true)}
          className="p-2.5 rounded-lg hover:bg-white/10 transition-all"
          title="Send Reward"
        >
          <Heart className="w-5 h-5 md:w-4 md:h-4 text-white" />
        </button>

        {/* Layout Picker - Hidden for webinar attendees and on mobile */}
        {!isWebinarViewer && (
          <>
          <div className="w-px h-5 bg-white/10 mx-2" />
          <div className="relative popover-container">
            <button
              ref={layoutBtnRef}
              onClick={(e) => {
                e.stopPropagation();
                if (layoutBtnRef.current) {
                  const rect = layoutBtnRef.current.getBoundingClientRect();
                  setLayoutPopupPos({
                    x: rect.right - 224, // 224 = w-56 = 14rem
                    y: rect.top - 8
                  });
                }
                // Close other popups
                setShowMicPicker(false);
                setShowSpeakerPicker(false);
                setShowCameraPicker(false);
                setShowShareOptions(false);
                setShowTranslation(false);
                setShowLayoutPicker(!showLayoutPicker);
              }}
              className="hidden sm:block p-2.5 rounded-lg hover:bg-white/10 transition-all"
              title="Layout"
            >
              <LayoutGrid className="w-5 h-5 md:w-4 md:h-4 text-white" />
            </button>
          </div>
          </>
        )}

        {/* Layout Picker Popup - Rendered as Portal */}
        {!isWebinarViewer && showLayoutPicker && ReactDOM.createPortal(
          <div
            className="fixed w-56 p-2 bg-[#001233]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl z-[99999]"
            style={{
              left: layoutPopupPos.x,
              bottom: window.innerHeight - layoutPopupPos.y,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-2 gap-1.5">
              {layouts.map((layout) => (
                <button
                  key={layout.id}
                  onClick={() => {
                    onLayoutChange?.(layout.id);
                    setShowLayoutPicker(false);
                  }}
                  className={`p-2 rounded-lg text-center transition-all ${
                    currentLayout === layout.id
                      ? 'bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white'
                      : 'bg-white/5 hover:bg-white/10 text-gray-300'
                  }`}
                >
                  <div className="text-lg">{layout.icon}</div>
                  <div className="text-[10px]">{layout.name}</div>
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}

        {/* Invite - Hidden on mobile */}
        <button
          onClick={onInvite}
          className="hidden sm:block p-2.5 rounded-lg hover:bg-white/10 transition-all"
          title="Invite"
        >
          <UserPlus className="w-4 h-4 text-white" />
        </button>

        {/* Merch Store — host/co-host/moderator can always see it to open the
            panel (which contains the "Show to attendees" toggle). Attendees
            only see this button once the host has turned the store on. */}
        {(canControlMerchStore || merchStoreActive) && (
          <button
            onClick={handleMerchStoreClick}
            className={`hidden sm:block p-2.5 rounded-lg transition-all ${
              showMerchStore ? 'bg-[#6a1fbf] hover:bg-[#6a1fbf]/80' : 'hover:bg-white/10'
            }`}
            title="Merchandise Store"
          >
            <ShoppingBag className="w-4 h-4 text-white" />
          </button>
        )}

        {/* Captions & Transcript - Single CC button like prototype (Hidden on mobile) */}
        <button
          ref={translationBtnRef}
          onClick={(e) => {
            e.stopPropagation();
            if (translationBtnRef.current) {
              const rect = translationBtnRef.current.getBoundingClientRect();
              setTranslationPopupPos({
                x: rect.right - 400,
                y: rect.top - 8
              });
            }
            setShowMicPicker(false);
            setShowSpeakerPicker(false);
            setShowCameraPicker(false);
            setShowShareOptions(false);
            setShowLayoutPicker(false);
            setShowSpokenLangDropdown(false);
            setShowCaptionLangDropdown(false);
            setShowTranslation(!showTranslation);
          }}
          className={`hidden sm:flex items-center justify-center p-2.5 rounded-lg transition-all ${
            isAICaptionsActive
              ? 'bg-[#6a1fbf] hover:bg-[#6a1fbf]/80'
              : 'hover:bg-white/10'
          }`}
          title={isAICaptionsActive ? 'Disable AI Captions' : 'Enable AI Captions'}
        >
          <span className="inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white border border-white/60 rounded leading-none">CC</span>
        </button>

        {/* Transcription / AI Notetaker — host & co-host only (Hidden on mobile) */}
        {canToggleTranscription && (
          <button
            ref={transcriptionBtnRef}
            onClick={(e) => {
              e.stopPropagation();
              if (transcriptionBtnRef.current) {
                const rect = transcriptionBtnRef.current.getBoundingClientRect();
                setTranscriptionPopupPos({
                  x: rect.right - 288,
                  y: rect.top - 8,
                });
              }
              setShowMicPicker(false);
              setShowSpeakerPicker(false);
              setShowCameraPicker(false);
              setShowShareOptions(false);
              setShowLayoutPicker(false);
              setShowTranslation(false);
              setShowTranscriptionPopup(!showTranscriptionPopup);
            }}
            className={`hidden sm:flex items-center justify-center p-2.5 rounded-lg transition-all ${
              isTranscriptionActive
                ? 'bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90'
                : 'hover:bg-white/10'
            }`}
            title={isTranscriptionActive ? 'Stop Transcription' : 'Start Transcription'}
          >
            <FileText className="w-4 h-4 text-white" />
          </button>
        )}

        {/* Transcription Controls Popup - Rendered as Portal */}
        {showTranscriptionPopup && canToggleTranscription && ReactDOM.createPortal(
          <div
            ref={transcriptionPopupRef}
            className="fixed w-72 bg-[#001233]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-[99999]"
            style={{
              left: transcriptionPopupPos.x,
              bottom: window.innerHeight - transcriptionPopupPos.y,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-4 pb-3 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white">Transcription Controls</h3>
            </div>
            <div className="p-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleTranscription?.();
                  setShowTranscriptionPopup(false);
                }}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                  isTranscriptionActive
                    ? 'bg-gradient-to-r from-[#6a1fbf]/20 to-[#00c853]/20 border-[#00c853]/40 hover:from-[#6a1fbf]/30 hover:to-[#00c853]/30'
                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isTranscriptionActive
                    ? 'bg-gradient-to-br from-[#6a1fbf] to-[#00c853]'
                    : 'bg-white/10'
                }`}>
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium">
                    {isTranscriptionActive ? 'Stop Transcription' : 'Start Transcription'}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {isTranscriptionActive ? 'Stop recording transcript' : 'Begin recording transcript'}
                  </p>
                </div>
                {isTranscriptionActive && (
                  <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0" />
                )}
              </button>
            </div>
          </div>,
          document.body
        )}

        {/* Captions & Transcript Popup - Rendered as Portal */}
        {showTranslation && ReactDOM.createPortal(
          <div
            ref={translationPopupRef}
            className="fixed w-[400px] bg-[#001233]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-[99999]"
            style={{
              left: translationPopupPos.x,
              bottom: window.innerHeight - translationPopupPos.y,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 pt-4 pb-3 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white">Captions & Transcript</h3>
            </div>

            {/* Content */}
            <div className="px-5 pb-5 space-y-4 pt-4">
              {/* Live Captions & Transcript Toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center gap-2">
                  <Languages className="w-4 h-4 text-[#6a1fbf]" />
                  <div>
                    <p className="text-sm text-white font-medium">Live Captions & Transcript</p>
                    <p className="text-[10px] text-gray-500">Speech-to-text for everyone in the call</p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartAICaptions?.();
                  }}
                  className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
                    isAICaptionsActive ? 'bg-gradient-to-r from-[#6a1fbf] to-[#00c853]' : 'bg-gray-600'
                  } cursor-pointer`}
                  role="switch"
                  aria-checked={!!isAICaptionsActive}
                >
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    isAICaptionsActive ? 'translate-x-[22px]' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {/* Language Settings */}
              <div className="pt-3 border-t border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Languages className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-400 font-medium">Language Settings</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {/* Spoken Language */}
                  <div className="relative">
                    <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 block">Spoken</label>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSpokenLangDropdown(!showSpokenLangDropdown);
                        setShowCaptionLangDropdown(false);
                      }}
                      className="w-full flex items-center justify-between bg-[#0a1628] border border-white/15 rounded-lg px-2.5 py-2 text-white text-xs hover:bg-white/10 hover:border-white/20 transition-all"
                    >
                      <span className="flex items-center gap-1.5 truncate">
                        {getFlagDisplay(getLanguageById(spokenLanguage)?.flag, getLanguageById(spokenLanguage)?.shortCode)}
                        <span className="truncate">{getLanguageById(spokenLanguage)?.label}</span>
                      </span>
                      <ChevronUp className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${showSpokenLangDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showSpokenLangDropdown && (
                      <div
                        className="absolute left-0 right-0 bottom-full mb-1 bg-[#0a1628] border border-white/15 rounded-lg shadow-xl z-[100000] flex flex-col"
                        style={{ maxHeight: '15rem' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="sticky top-0 p-1.5 bg-[#0a1628] border-b border-white/10 flex items-center gap-1.5">
                          <Search className="w-3 h-3 text-gray-500 flex-shrink-0 ml-1" />
                          <input
                            type="text"
                            autoFocus
                            value={spokenLangQuery}
                            onChange={(e) => setSpokenLangQuery(e.target.value)}
                            placeholder="Search languages..."
                            className="flex-1 bg-white text-black text-xs placeholder:text-gray-500 rounded px-1.5 py-0.5 focus:outline-none"
                          />
                        </div>
                        <div className="overflow-y-auto">
                          {filteredSpokenLanguages.length === 0 && (
                            <div className="px-2.5 py-2 text-xs text-gray-500">No matches</div>
                          )}
                          {filteredSpokenLanguages.map(lang => (
                            <button
                              key={lang.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSpokenLanguageChange(lang.id);
                                setShowSpokenLangDropdown(false);
                                setSpokenLangQuery('');
                              }}
                              className={`w-full flex items-center gap-2 px-2.5 py-2 text-xs text-left transition-colors ${
                                spokenLanguage === lang.id
                                  ? 'bg-[#6a1fbf]/20 text-white'
                                  : 'text-gray-300 hover:bg-white/[0.06]'
                              }`}
                            >
                              {getFlagDisplay(lang.flag, lang.shortCode)}
                              <span className="truncate flex-1">{lang.label}</span>
                              {spokenLanguage === lang.id && <Check className="w-3 h-3 text-[#00c853] flex-shrink-0" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Caption Language */}
                  <div className="relative">
                    <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 block">Captions</label>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCaptionLangDropdown(!showCaptionLangDropdown);
                        setShowSpokenLangDropdown(false);
                      }}
                      className="w-full flex items-center justify-between bg-[#0a1628] border border-white/15 rounded-lg px-2.5 py-2 text-white text-xs hover:bg-white/10 hover:border-white/20 transition-all"
                    >
                      <span className="flex items-center gap-1.5 truncate">
                        {getFlagDisplay(getLanguageById(captionLanguage)?.flag, getLanguageById(captionLanguage)?.shortCode)}
                        <span className="truncate">{getLanguageById(captionLanguage)?.label}</span>
                      </span>
                      <ChevronUp className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${showCaptionLangDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showCaptionLangDropdown && (
                      <div
                        className="absolute left-0 right-0 bottom-full mb-1 bg-[#0a1628] border border-white/15 rounded-lg shadow-xl z-[100000] flex flex-col"
                        style={{ maxHeight: '15rem' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="sticky top-0 p-1.5 bg-[#0a1628] border-b border-white/10 flex items-center gap-1.5">
                          <Search className="w-3 h-3 text-gray-500 flex-shrink-0 ml-1" />
                          <input
                            type="text"
                            autoFocus
                            value={captionLangQuery}
                            onChange={(e) => setCaptionLangQuery(e.target.value)}
                            placeholder="Search languages..."
                            className="flex-1 bg-white text-black text-xs placeholder:text-gray-500 rounded px-1.5 py-0.5 focus:outline-none"
                          />
                        </div>
                        <div className="overflow-y-auto">
                          {filteredCaptionLanguages.length === 0 && (
                            <div className="px-2.5 py-2 text-xs text-gray-500">No matches</div>
                          )}
                          {filteredCaptionLanguages.map(lang => (
                            <button
                              key={lang.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCaptionLanguageChange(lang.id);
                                setShowCaptionLangDropdown(false);
                                setCaptionLangQuery('');
                              }}
                              className={`w-full flex items-center gap-2 px-2.5 py-2 text-xs text-left transition-colors ${
                                captionLanguage === lang.id
                                  ? 'bg-[#6a1fbf]/20 text-white'
                                  : 'text-gray-300 hover:bg-white/[0.06]'
                              }`}
                            >
                              {getFlagDisplay(lang.flag, lang.shortCode)}
                              <span className="truncate flex-1">{lang.label}</span>
                              {captionLanguage === lang.id && <Check className="w-3 h-3 text-[#00c853] flex-shrink-0" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Text Translator Button */}
              <button
                onClick={() => {
                  onOpenTranslationTools?.();
                  setShowTranslation(false);
                }}
                className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl font-medium bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10 transition-all"
              >
                <Languages className="w-4 h-4" />
                <span>Text Translator</span>
              </button>
            </div>
          </div>,
          document.body
        )}

        {/* Settings - Hidden on mobile.
            Reverted from showing on mobile because SettingsModal isn't
            responsive yet. Re-enable once the modal supports mobile layouts. */}
        <button
          onClick={() => setShowSettingsModal(true)}
          className="hidden sm:block p-2.5 rounded-lg hover:bg-white/10 transition-all"
          title="Settings"
        >
          <Settings className="w-5 h-5 md:w-4 md:h-4 text-white" />
        </button>

        {/* Go Live Button - prominent for live stream sessions */}
        {showGoLiveButton && (
          <button
            onClick={handleGoLiveClick}
            className={`flex items-center gap-1.5 px-4 py-3.5 md:px-4 md:py-2.5 rounded-lg transition-all font-medium text-sm ${
              isLiveStreaming
                ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
                : 'bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 text-white'
            }`}
            title={isLiveStreaming ? 'Live Streaming Active' : 'Go Live'}
          >
            <Radio className="w-5 h-5 md:w-4 md:h-4" />
            <span className="hidden sm:inline">{isLiveStreaming ? 'LIVE' : 'Go Live'}</span>
          </button>
        )}

        <div className="w-px h-5 bg-white/10 mx-2" />

        {/* Exit Meeting Button */}
        {isHost ? (
          <>
            <button
              ref={exitBtnRef}
              onClick={() => {
                setShowExitMenu((prev) => !prev);
                setShowAssignHost(false);
              }}
              className="p-2.5 rounded-lg bg-red-500/80 hover:bg-red-500 transition-all"
              title={meetingSettings?.roomType === "Webinar" ? "End Webinar" : "End Meeting"}
            >
              <ExitIcon className="w-5 h-5" fillcolor="white" />
            </button>
            {showExitMenu && ReactDOM.createPortal(
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-[9998]"
                  onClick={() => { setShowExitMenu(false); setShowAssignHost(false); }}
                />
                {/* Popup menu - positioned above the exit button */}
                <div
                  className="fixed z-[9999] w-72 p-3 rounded-xl border border-white/10 bg-[#001233]/95 backdrop-blur-xl shadow-2xl"
                  style={{
                    bottom: exitBtnRef.current ? (window.innerHeight - exitBtnRef.current.getBoundingClientRect().top + 8) : 80,
                    right: exitBtnRef.current ? (window.innerWidth - exitBtnRef.current.getBoundingClientRect().right) : 16,
                  }}
                >
                  {!showAssignHost ? (
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-semibold text-sm mb-1 text-white">{meetingSettings?.roomType === "Webinar" ? "End Webinar" : "End Meeting"}</h4>
                        <p className="text-xs text-gray-400">Choose how you'd like to end the {meetingSettings?.roomType === "Webinar" ? "webinar" : "meeting"}</p>
                      </div>
                      <div className="space-y-2">
                        {/* End for All */}
                        <button
                          onClick={() => {
                            onEndForAll?.();
                            setShowExitMenu(false);
                          }}
                          className="w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors hover:bg-red-500/10 border border-red-500/30 flex items-start gap-3"
                        >
                          <LogOut className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-medium text-red-400">End {meetingSettings?.roomType === "Webinar" ? "Webinar" : "Meeting"} for All</div>
                            <div className="text-xs text-gray-400">End the {meetingSettings?.roomType === "Webinar" ? "webinar" : "meeting"} and remove all participants</div>
                          </div>
                        </button>
                        {/* End for All Except Host */}
                        <button
                          onClick={() => {
                            const hasCoHosts = coHosts && coHosts.size > 0;
                            const hasPresenters = presenters && presenters.size > 0;
                            if (!hasCoHosts && !hasPresenters) {
                              // Host only — show warning modal
                              setShowEndExceptHostWarning(true);
                            } else {
                              // Host + co-hosts/presenters — kick everyone except host, co-hosts, presenters
                              onEndForAllExceptHost?.();
                            }
                            setShowExitMenu(false);
                          }}
                          className="w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors hover:bg-white/5 border border-white/10 flex items-start gap-3"
                        >
                          <UserX className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-medium text-white">End {meetingSettings?.roomType === "Webinar" ? "Webinar" : "Meeting"} for All, Except Host</div>
                            <div className="text-xs text-gray-400">Remove all participants but stay in the {meetingSettings?.roomType === "Webinar" ? "webinar" : "meeting"}</div>
                          </div>
                        </button>
                        {/* Leave & Assign Host */}
                        <button
                          onClick={() => {
                            if (remoteParticipants.length === 0) {
                              onLeaveAndAssignHost?.();
                              setShowExitMenu(false);
                            } else if (remoteParticipants.length === 1) {
                              onLeaveAndAssignHost?.(remoteParticipants[0].id);
                              setShowExitMenu(false);
                            } else {
                              setShowAssignHost(true);
                            }
                          }}
                          className="w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors hover:bg-white/5 border border-white/10 flex items-start gap-3"
                        >
                          <User className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-medium text-white">Leave & Assign Host</div>
                            <div className="text-xs text-gray-400">Choose someone to continue hosting</div>
                          </div>
                        </button>
                        {/* Leave (co-host only) */}
                        {meetingRole === 'co-host' && (
                          <button
                            onClick={() => {
                              onLeave?.();
                              setShowExitMenu(false);
                            }}
                            className="w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors hover:bg-white/5 border border-white/10 flex items-start gap-3"
                          >
                            <LogOut className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="font-medium text-white">Leave</div>
                              <div className="text-xs text-gray-400">Leave the {meetingSettings?.roomType === "Webinar" ? "webinar" : "meeting"} without ending it</div>
                            </div>
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <button
                          onClick={() => setShowAssignHost(false)}
                          className="text-xs text-gray-400 hover:text-white transition-colors mb-1"
                        >
                          ← Back
                        </button>
                        <h4 className="font-semibold text-sm mb-1 text-white">Assign New Host</h4>
                        <p className="text-xs text-gray-400">Select a participant to become the new host</p>
                      </div>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {remoteParticipants.map((p) => (
                          <button
                            key={p.id}
                            onClick={() => {
                              onLeaveAndAssignHost?.(p.id);
                              setShowExitMenu(false);
                              setShowAssignHost(false);
                            }}
                            className="w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors hover:bg-white/10 border border-white/10 flex items-center gap-3"
                          >
                            <div className="w-7 h-7 rounded-full bg-[#6a1fbf]/30 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs text-white font-medium">
                                {(p.displayName || '?').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="text-white truncate">{p.displayName}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>,
              document.body
            )}
          </>
        ) : (
          <button
            onClick={onLeave}
            className="p-2.5 rounded-lg bg-red-500/80 hover:bg-red-500 transition-all"
            title="Leave Meeting"
          >
            <ExitIcon className="w-5 h-5" fillcolor="white" />
          </button>
        )}
      </div>

      {/* Merch Store Panel — Phase 1: real showcase data + picker */}
      <MerchStorePanel
        open={showMerchStore}
        onClose={() => setShowMerchStore(false)}
        canControl={canControlMerchStore}
        active={merchStoreActive}
        onToggleActive={handleToggleMerchStoreActive}
        products={displayedProducts}
        availableShowcases={availableShowcases}
        selectedShowcaseId={selectedShowcaseId}
        onSelectShowcase={handleSelectShowcase}
      />

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        audioInputs={audioInputs}
        videoInputs={videoInputs}
        audioOutputs={audioOutputs}
        selectedAudioInput={selectedAudioInput}
        selectedVideoInput={selectedVideoInput}
        selectedAudioOutput={selectedAudioOutput}
        onAudioInputChange={onAudioInputChange}
        onVideoInputChange={onVideoInputChange}
        onAudioOutputChange={onAudioOutputChange}
        // Room context for virtual backgrounds
        roomId={roomId}
        // Virtual background props
        blurEnabled={blurEnabled}
        setBlurEnabled={setBlurEnabled}
        blurStrength={blurStrength}
        setBlurStrength={setBlurStrength}
        selectedBackground={selectedBackground}
        handleStartVirtualBackground={handleStartVirtualBackground}
        virtualBackgroundOn={virtualBackgroundOn}
        // Green screen props
        greenScreenEnabled={greenScreenEnabled}
        handleStartGreenScreen={handleStartGreenScreen}
        // Local webcam stream for preview
        localWebcamStream={localWebcamStream}
        localWebcamOn={localWebcamOn}
        processedWebcamStream={processedWebcamStream}
        // Meeting stats for Statistics tab (ref to avoid re-renders)
        localParticipantStatsRef={localParticipantStatsRef}
        // Camera resolution callback
        onCameraResolutionChange={onCameraResolutionChange}
        // Audio settings callback
        onAudioSettingsChange={onAudioSettingsChange}
        // AI Noise cancellation props
        noiseCancellationEnabled={noiseCancellationEnabled}
        noiseCancellationMode={noiseCancellationMode}
        onNoiseCancellationToggle={onNoiseCancellationToggle}
        onNoiseCancellationModeChange={onNoiseCancellationModeChange}
        // Host controls
        meetingRole={meetingRole}
        waitingRoomEnabled={waitingRoomEnabled}
        onWaitingRoomChange={handleWaitingRoomChange}
        autoAdmitRegistered={autoAdmitRegistered}
        onAutoAdmitRegisteredChange={handleAutoAdmitRegisteredChange}
        guestCanUnmute={meetingSettings?.guestCanUnmute}
        onGuestCanUnmuteChange={handleGuestCanUnmuteChange}
        muteOnEntry={meetingSettings?.muteParticipants}
        onMuteOnEntryChange={handleMuteOnEntryChange}
        webinarMode={meetingSettings?.roomType === "Webinar"}
      />

      {/* Reward Modal */}
      <RewardModal
        isOpen={showRewardModal}
        onClose={() => setShowRewardModal(false)}
        recipientName="Host"
        userTokenBalance={100}
        onSendReward={(data) => {
          console.log('Reward sent:', data);
          // TODO: Integrate with backend API to process reward
        }}
      />

      {/* End Meeting Except Host — Warning Modal (host only, no co-hosts/presenters) */}
      {showEndExceptHostWarning && (
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="relative bg-[#001233] border border-white/10 rounded-2xl p-6 mx-4 max-w-sm w-full shadow-2xl">
            <button
              onClick={() => setShowEndExceptHostWarning(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <UserX className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-white text-lg font-semibold">
                No Co-host or Presenter
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                You are the only host in this {meetingSettings?.roomType === "Webinar" ? "webinar" : "meeting"}. Would you like to end the entire {meetingSettings?.roomType === "Webinar" ? "webinar" : "meeting"} or just remove all participants?
              </p>
              <div className="flex flex-col gap-2 w-full mt-2">
                <button
                  onClick={() => {
                    setShowEndExceptHostWarning(false);
                    onEndForAll?.();
                  }}
                  className="w-full py-2.5 text-sm bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 font-medium hover:bg-red-500/30 transition-colors"
                >
                  Yes, End Entire {meetingSettings?.roomType === "Webinar" ? "Webinar" : "Meeting"}
                </button>
                <button
                  onClick={() => {
                    setShowEndExceptHostWarning(false);
                    onEndForAllExceptHost?.();
                  }}
                  className="w-full py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white font-medium hover:bg-white/10 transition-colors"
                >
                  No, Just Remove All Participants
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right section: Pagination + Call Duration Timer */}
      <div className="hidden sm:flex items-center justify-end min-w-[120px] gap-3">
        {/* Grid Pagination Controls */}
        {gridPagination.totalPages > 1 && (
          <div className="flex items-center gap-3 bg-gray-900/90 backdrop-blur-sm rounded-full px-3 py-1.5 border border-gray-700/60">
            <button
              onClick={() => gridPagination.goToPage?.(Math.max(0, gridPagination.currentPage - 1))}
              disabled={gridPagination.currentPage === 0}
              className={`flex items-center justify-center w-7 h-7 rounded-full transition-all duration-200 ${
                gridPagination.currentPage === 0
                  ? 'bg-gray-700 cursor-not-allowed opacity-40'
                  : 'bg-[#7700ff] hover:bg-[#9933ff] cursor-pointer'
              }`}
              style={{ color: '#ffffff' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
              </svg>
            </button>
            <div className="flex items-center gap-1 text-white text-sm font-medium px-1">
              <span>{gridPagination.currentPage + 1}</span>
              <span className="text-gray-400">/</span>
              <span>{gridPagination.totalPages}</span>
            </div>
            <button
              onClick={() => gridPagination.goToPage?.(Math.min(gridPagination.totalPages - 1, gridPagination.currentPage + 1))}
              disabled={gridPagination.currentPage >= gridPagination.totalPages - 1}
              className={`flex items-center justify-center w-7 h-7 rounded-full transition-all duration-200 ${
                gridPagination.currentPage >= gridPagination.totalPages - 1
                  ? 'bg-gray-700 cursor-not-allowed opacity-40'
                  : 'bg-[#7700ff] hover:bg-[#9933ff] cursor-pointer'
              }`}
              style={{ color: '#ffffff' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5 15.75 12l-7.5 7.5" />
              </svg>
            </button>
          </div>
        )}
        {/* Call Duration Timer - hidden when right sidebar expanded */}
        {rightSidebarCollapsed && meetingStartTime && (
          <CallDurationTimer startTime={meetingStartTime} inline={true} />
        )}
      </div>
    </div>
  );
}
